import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Spacer,
  Text,
  useToast
} from "@chakra-ui/react";
import { Card } from "../../../../../components/Card";
import { Chart } from "../../../../Dashboard/components";
import { useState } from "react";
import { showErrorToast, showWarnToast, translateSubElementString } from "../../../../../utils";
import {
  scenarioPlanningKeys,
  useCalculateScenarioPlanning,
  useExportScenarioPlanningChartData,
  useGetDecarbonizationPathways,
  useGetScenarioPlanningParameters
} from "../../../../../hooks/query";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { SliderWithTitle } from "../../../../../components/Forms";
import { NumberField } from "../../../../../components/NumberField";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { StatusCodes } from "http-status-codes";

const PATHWAYS_OPTION_OFF = "OFF";

function CarbonPricingFinancialModel() {
  const { t } = useTranslation("translation", {
    keyPrefix: "scenarioPlanning"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    data: parameters,
    error: parametersError,
    isFetching: isParametersFetching,
    isSuccess
  } = useGetScenarioPlanningParameters({
    onSuccess: (params) => {
      setUsePathways(params.pathwayId ?? PATHWAYS_OPTION_OFF);
      setReductionRate(params.reductionRate);
      setGrowthRate(params.growthRate);
      setAverageCo2Price(params.averageCo2Price);
    },
    onError: err => handleErrors(err),
    cacheTime: 0
  });

  const {
    data: pathways
  } = useGetDecarbonizationPathways({
    onError: err => handleErrors(err),
    cacheTime: 0
  });

  const [usePathways, setUsePathways] = useState(parameters?.pathwayId ?? PATHWAYS_OPTION_OFF);
  const [reductionRate, setReductionRate] = useState(parameters?.reductionRate ?? 50);
  const [growthRate, setGrowthRate] = useState(parameters?.growthRate ?? 10);
  const [averageCo2Price, setAverageCo2Price] = useState(parameters?.averageCo2Price ?? 100);
  const [chartData, setChartData] = useState();

  const handlePathwayChange = (e) => {
    setUsePathways(e.target.value);
    setReductionRate(parameters.reductionRate);
    setGrowthRate(parameters.growthRate);
  };

  const {
    isFetching: isCalculationFetching,
    error: calculateError,
    refetch: refetchCalculation
  } = useCalculateScenarioPlanning({
    pathwayId: usePathways !== PATHWAYS_OPTION_OFF ? usePathways : null,
    reductionRate,
    growthRate,
    averageCo2Price,
    enabled: isSuccess
  }, {
    onError: err => handleErrors(err),
    onSuccess: (response) => {
      setChartData(response.data);
      if(response.status === StatusCodes.PARTIAL_CONTENT) {
        showWarnToast(toast, response.data?.missingDataMessage + " " + globalTranslation("contactAdministrator"));
      }
    },
    cacheTime: 0
  });

  const {
    mutate: exportChartData
  } = useExportScenarioPlanningChartData({
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }

    if (err.response?.status === 400) {
      if (!averageCo2Price) {
        showErrorToast(toast, t("errors.averageCarbonPriceRequired"));
      }
      return;
    }

    showErrorToast(toast, err.message);
  };

  const onSubmit = () => {
    refetchCalculation().then(() => queryClient.invalidateQueries(scenarioPlanningKeys.parameters()));
  };

  const handleExportChartData = () => {
    dispatch(showAppSpinner());
    exportChartData({ params: {
        pathwayId: usePathways !== PATHWAYS_OPTION_OFF ? usePathways : null, reductionRate, growthRate, averageCo2Price
      }});
  };

  const generatePathwayOptions = () => {
    if (pathways && pathways.length > 0) {
      return pathways.map(pathway => {
        return <option key={`pathway-option-${pathway.id}`} value={pathway.id}>
          {pathway.name}
        </option>;
      });
    }
    return [];
  };

  return (
    <Grid templateColumns={{ base: "1fr", xl: "2fr 4fr" }} gap="5">
      <GridItem>
        <Card>
          <Heading size="md">{t("labels.scenarioBuilder")}</Heading>
          <Text size="sm" color="esg_gray.300" marginBlock={2}>
            {t("labels.scenarioBuilderSubTitle")}
          </Text>
          <Divider marginBlock={5} />
          <FormLabel
            htmlFor="useDecarbonizationPathways"
            whiteSpace="nowrap"
            marginBlockEnd="0"
            blockSize="5"
            isTruncated
            color="esg_gray.200"
            title={t("labels.useDecarbonizationPathways")}
            fontWeight="bold"
            fontSize="sm"
          >
            {t("labels.useDecarbonizationPathways")}
          </FormLabel>
          <Select
            id={"useDecarbonizationPathways"}
            minW="150px"
            borderRadius="none"
            value={usePathways}
            title={usePathways}
            onChange={(e) => handlePathwayChange(e)}
            marginBlockStart={1}
          >
            <option value={PATHWAYS_OPTION_OFF}>
              {t(`labels.${PATHWAYS_OPTION_OFF}`)}
            </option>
            {generatePathwayOptions()}
          </Select>
          {usePathways === PATHWAYS_OPTION_OFF && <SliderWithTitle
            title={t("labels.emissionsReductionTitle")}
            subTitle={t("labels.emissionsReductionSubTitle")}
            value={reductionRate}
            setValue={setReductionRate}
            isDisabled={calculateError?.response?.status === 404 || isParametersFetching || isCalculationFetching}
          />}
          {usePathways === PATHWAYS_OPTION_OFF && <SliderWithTitle
            title={t("labels.growthRateTitle")}
            subTitle={t("labels.growthRateSubTitle")}
            value={growthRate}
            setValue={setGrowthRate}
            isDisabled={calculateError?.response?.status === 404 || isParametersFetching || isCalculationFetching}
          />}

          <Box marginBlockStart={10}>
            <Flex direction="column" gap={2}>
              <Heading size="sm">
                {translateSubElementString("scenarioPlanning.labels.averageCarbonPriceTitle")}
              </Heading>
              <Text size="sm" color="esg_gray.300">
                {t("labels.averageCarbonPriceSubTitle")}
              </Text>
              <Input backgroundColor="white" borderRadius="none"
                     marginBlockStart={1} value={averageCo2Price}
                     onChange={(event) => setAverageCo2Price(event.target.value)}
                     isDisabled={calculateError?.response?.status === 404 || isParametersFetching || isCalculationFetching}
                     type="number"
                     step="0.001"
                     onWheel={e => e.target.blur()}
              />
            </Flex>
          </Box>

          <Divider marginBlock={5} />
          <Flex justify="flex-end">
            <Button
              colorScheme="customScheme"
              width="32"
              type="submit"
              onClick={onSubmit}
              isDisabled={calculateError?.response?.status === 404 || isParametersFetching || isCalculationFetching}
            >
              {t("buttons.apply")}
            </Button>
          </Flex>
        </Card>
      </GridItem>

      <GridItem>
        <Card>
          <Flex justifyContent="space-between">
            <Box>
              <Heading size="md">{t("labels.carbonPricingFinancialModelTitle")}</Heading>
              <Text size="sm" color="esg_gray.300" marginBlock={2}>
                {t("labels.carbonPricingFinancialModelSubTitle")}
              </Text>
            </Box>
            {!parametersError && calculateError?.response?.status !== 404 && chartData && chartData.data &&
              <Button
                colorScheme="customScheme"
                width="32"
                onClick={handleExportChartData}
              >
                {t("buttons.export")}
              </Button>
            }
          </Flex>

          <Divider marginBlock={5} />

          {
            parametersError || calculateError?.response?.status === 404 ?
              <Text fontSize="lg" fontWeight="bold">
                {t("labels.noData")}
              </Text> :
              (<>
                <Flex pl={10} pr={10}>
                  <Box>
                    <Text fontSize="sm" color="esg_gray.300">{t("labels.baseYear")}</Text>
                    <Text fontSize="lg">{chartData?.year}</Text>
                  </Box>
                  <Spacer />
                  <Box>
                    <Text fontSize="sm" color="esg_gray.300">{t("labels.baseline")}</Text>
                    <Flex gap={1}>
                      <NumberField fontSize="lg" value={Math.round(chartData?.co2BaseLine)} />
                      <Text>{translateSubElementString("scenarioPlanning.labels.tCO2e")}</Text>
                    </Flex>
                  </Box>
                </Flex>

                <Chart blockSize={96}
                       marginBlockStart={5}
                       data={chartData?.data}
                       legends={[
                         {
                           anchor: "bottom",
                           direction: "row",
                           justify: false,
                           translateX: 20,
                           translateY: 80,
                           itemsSpacing: 0,
                           itemDirection: "left-to-right",
                           itemWidth: 150,
                           itemHeight: 20,
                           itemOpacity: 1,
                           symbolSize: 12,
                           symbolShape: "circle",
                           symbolBorderColor: "rgba(9, 9, 9, .5)",
                           effects: [
                             {
                               on: "hover",
                               style: {
                                 itemBackground: "rgba(0, 0, 0, .03)",
                                 itemOpacity: 1
                               }
                             }
                           ]
                         }
                       ]}
                />
              </>)}
        </Card>
      </GridItem>
    </Grid>
  );
}

export default CarbonPricingFinancialModel;